
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, BellOff, Trash2, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SavedSearch {
  id: string;
  name: string;
  filters: any;
  notifications_enabled: boolean;
  created_at: string;
  match_count: number;
}

const SavedSearches = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSavedSearches();
    }
  }, [user]);

  const fetchSavedSearches = async () => {
    try {
      // This would be implemented with a proper saved_searches table
      // For now, we'll simulate with localStorage
      const saved = JSON.parse(localStorage.getItem(`savedSearches_${user?.id}`) || '[]');
      setSavedSearches(saved);
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleNotifications = async (searchId: string, enabled: boolean) => {
    try {
      const updated = savedSearches.map(search => 
        search.id === searchId 
          ? { ...search, notifications_enabled: enabled }
          : search
      );
      setSavedSearches(updated);
      localStorage.setItem(`savedSearches_${user?.id}`, JSON.stringify(updated));
      
      toast.success(
        enabled 
          ? t('notifications_enabled', 'تم تفعيل الإشعارات', 'Notifications enabled')
          : t('notifications_disabled', 'تم إلغاء الإشعارات', 'Notifications disabled')
      );
    } catch (error) {
      toast.error(t('error_updating', 'خطأ في التحديث', 'Error updating'));
    }
  };

  const deleteSearch = async (searchId: string) => {
    try {
      const updated = savedSearches.filter(search => search.id !== searchId);
      setSavedSearches(updated);
      localStorage.setItem(`savedSearches_${user?.id}`, JSON.stringify(updated));
      toast.success(t('search_deleted', 'تم حذف البحث المحفوظ', 'Saved search deleted'));
    } catch (error) {
      toast.error(t('error_deleting', 'خطأ في الحذف', 'Error deleting'));
    }
  };

  if (!user) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          {t('saved_searches', 'البحث المحفوظ', 'Saved Searches')}
        </CardTitle>
        <CardDescription>
          {t('manage_saved_searches', 'إدارة عمليات البحث المحفوظة والإشعارات', 'Manage your saved searches and notifications')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded" />
            ))}
          </div>
        ) : savedSearches.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {t('no_saved_searches', 'لا توجد عمليات بحث محفوظة', 'No saved searches yet')}
          </p>
        ) : (
          <div className="space-y-4">
            {savedSearches.map((search) => (
              <div key={search.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium">{search.name}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">
                      {search.match_count} {t('matches', 'نتيجة', 'matches')}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(search.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleNotifications(search.id, !search.notifications_enabled)}
                  >
                    {search.notifications_enabled ? (
                      <Bell className="h-4 w-4 text-green-600" />
                    ) : (
                      <BellOff className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteSearch(search.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavedSearches;
